import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getSubscriptionStatus } from '../../services/stripeService';
import { Clock, AlertTriangle, Info, CheckCircle } from 'lucide-react';

const SubscriptionGuard = ({ children, fallback = null, signOut = null }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStatus = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const subscriptionStatus = await getSubscriptionStatus(user.id);
        setStatus(subscriptionStatus);
      } catch (error) {
        console.error('Error loading subscription status:', error);
        // If we can't load status, assume no access for security
        setStatus({ subscription_status: 'inactive', isTrialExpired: true });
      } finally {
        setLoading(false);
      }
    };

    loadStatus();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sws-light-cream">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-sws-purple" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-sws-navy">Checking subscription...</p>
          </div>
        </div>
      </div>
    );
  }

  const hasAccess = status && (
    status.subscription_status === 'active' ||
    (status.subscription_status === 'trial' && !status.isTrialExpired) ||
    status.subscription_status === 'past_due' ||  // Allow access during payment retry
    (status.subscription_status === 'canceled' && 
    status.subscription_end_date && 
    new Date(status.subscription_end_date) > new Date()) // Grace period
  );

  // Add warning banner for payment issues
  if (status?.subscription_status === 'past_due') {
    return (
      <>
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
          <div className="flex items-center">
            <AlertTriangle className="text-yellow-600 mr-3" size={20} />
            <div className="flex-1">
              <p className="text-yellow-800 font-medium">Payment Failed</p>
              <p className="text-yellow-700 text-sm">
                Please update your payment method to maintain access.
              </p>
            </div>
            <button 
              onClick={() => window.location.href = '/update-payment'}
              className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
            >
              Update Payment
            </button>
          </div>
        </div>
        {children}
      </>
    );
  }

  if (status?.subscription_status === 'canceled' && status?.subscription_end_date) {
    const endDate = new Date(status.subscription_end_date);
    const daysRemaining = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining > 0) {
      return (
        <>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <div className="flex items-center">
              <Info className="text-blue-600 mr-3" size={20} />
              <div className="flex-1">
                <p className="text-blue-800 font-medium">Subscription Ending Soon</p>
                <p className="text-blue-700 text-sm">
                  Your subscription will end in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}. 
                  Reactivate to keep your access.
                </p>
              </div>
              <button 
                onClick={() => {
                  // Use a full URL to force the browser to include the hash
                  const baseUrl = window.location.origin;
                  window.location.href = `${baseUrl}/pricing#pricing-section`;
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Reactivate Subscription
              </button>
            </div>
          </div>
          {children}
        </>
      );
    }
  }

  if (!hasAccess) {
    return fallback || <SubscriptionRequired status={status} signOut={signOut} />;
  }

  return children;
};

const SubscriptionRequired = ({ status, signOut }) => {
  const isTrialExpired = status?.isTrialExpired;
  const isInTrial = status?.subscription_status === 'trial';
  const subscriptionStatus = status?.subscription_status;

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        backgroundImage: 'linear-gradient(to bottom, #f5f2f0, #ffffff)' 
      }}
    >
      <div className="relative max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Gradient border effect */}
        <div 
          className="absolute inset-0"
          style={{ 
            backgroundImage: 'linear-gradient(to bottom right, rgba(189, 180, 226, 0.1), rgba(44, 54, 94, 0.1))' 
          }}
        />
        
        <div className="relative z-10">
          {/* Logo Section - Restored */}
          <div className="bg-white border-b border-gray-100 py-8 px-6 text-center">
            <div className="text-2xl font-bold text-sws-navy">
              Profit Clarity
            </div>
            <div className="text-xs text-sws-slate mt-2">
              Profitability Intelligence for Business Owners
            </div>
          </div>

          {/* Header - Softer purple gradient with proper status */}
          <div 
            className="py-10 px-8 text-center text-white"
            style={{ 
              backgroundImage: 'linear-gradient(135deg, #667eea, #764ba2)' 
            }}
          >
            <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 backdrop-blur rounded-full mb-3">
              <Clock size={28} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {isTrialExpired ? 'Trial Expired' : 'Subscription Required'}
            </h2>
            <p className="text-white/90 text-sm">
              {isTrialExpired 
                ? 'Your 14-day free trial has ended'
                : subscriptionStatus === 'inactive' 
                  ? 'Active subscription needed to continue'
                  : 'Activate your subscription to access Profit Clarity'
              }
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {isTrialExpired && isInTrial && (
              <div 
                className="rounded-xl p-4 mb-5"
                style={{ 
                  backgroundImage: 'linear-gradient(to right, rgba(34, 197, 94, 0.1), rgba(16, 185, 129, 0.1))' 
                }}
              >
                <div className="flex items-center">
                  <CheckCircle className="text-green-600 mr-3" size={20} />
                  <div>
                    <p className="text-green-800 font-medium">Trial Successfully Completed</p>
                    <p className="text-green-700 text-sm">
                      You've explored the full platform for 14 days
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Positive value proposition */}
            <div className="space-y-5 mb-7">
              <div 
                className="rounded-lg p-4"
                style={{ 
                  backgroundImage: 'linear-gradient(to right, rgba(189, 180, 226, 0.05), rgba(44, 54, 94, 0.05))' 
                }}
              >
                <h3 className="font-bold text-sws-navy text-lg mb-2">
                  You've experienced the power. Keep it going.
                </h3>
                <p className="text-sws-slate text-sm">
                  In just 14 days, you discovered insights about your business that most entrepreneurs 
                  never find. Continue building on that clarity.
                </p>
              </div>

              {/* What they can keep doing with access */}
              <div>
                <h4 className="font-semibold text-sws-navy mb-3">
                  Keep making data-driven decisions with:
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                      <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-sws-navy text-sm">Clear profitability analysis for every offer</p>
                      <p className="text-xs text-sws-slate">Know exactly which offers drive real profit and which don't.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                      <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-sws-navy text-sm">Hidden cost discovery and tracking</p>
                      <p className="text-xs text-sws-slate">Uncover and monitor the costs that silently drain profits.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                      <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-sws-navy text-sm">Strategic recommendations and insights</p>
                      <p className="text-xs text-sws-slate">Get actionable guidance on pricing, focus, and optimization.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                      <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-sws-navy text-sm">Professional reports and documentation</p>
                      <p className="text-xs text-sws-slate">Share clear profitability insights with stakeholders and advisors.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Clear, strong CTA */}
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/pricing'}
                className="w-full text-white py-4 px-6 rounded-xl font-bold text-lg hover:shadow-xl transform hover:scale-[1.02] transition-all"
                style={{ 
                  backgroundImage: 'linear-gradient(to right, #2c365e, #484d6d)' 
                }}
              >
                Continue Your Profit Analysis
              </button>
              
              <div className="text-center">
                <p className="text-xs text-sws-slate mb-3">
                  From $39/month • Cancel anytime • Your data is saved and waiting
                </p>
                
                {/* Logout option for developers/testers */}
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to sign out?')) {
                      if (signOut && typeof signOut === 'function') {
                        signOut();
                      } else {
                        // Fallback if signOut not provided
                        window.location.href = '/login';
                      }
                    }
                  }}
                  className="text-xs text-sws-slate hover:text-sws-navy underline"
                >
                  Sign out of this account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionGuard;